import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { useFormik } from 'formik'
import { FormStatusType } from '@/enums/form-status.enum'
import { useTranslation } from 'react-i18next'

import { useUsersActions, useUsersDialog } from './'
import { usersSelector } from '@/state/user/slices/index.slices'
import { REGEX_INPUT, REGEX_EMAIL, REGEX_USERNAME } from '@/constants/regex'

import { Brands } from '@/state/user/models'
import { getValidatePassword } from '@/utils'
import { DialogsEnum } from '@/enums'

export const useUsersForm = () => {
  const { t } = useTranslation()

  const { createUser, updateUser, setCurrentUserAction } = useUsersActions()

  const { currentUser } = useSelector(usersSelector)

  const { formMode, dialog, toggleDialog } = useUsersDialog()

  const handleCloseDialog = () => {
    toggleDialog(false, DialogsEnum.DIALOG_USERS)

    if (formMode === FormStatusType.EDIT) {
      setCurrentUserAction(null)
    }
  }

  const handleSubmit = () => {
    const { id, newPassword, ...create } = formikUser.values

    if (formMode === FormStatusType.CREATE) {
      createUser({
        ...create,
        brands: create.brands as any,
      })
    } else {
      const { password, ...update } = create
      updateUser({
        ...update,
        password: password !== '' ? password : undefined,
        id: currentUser.id,
        brands: update.brands as any,
      })
    }
  }

  const initialValuesUser = {
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
    password: '',
    newPassword: '',
    role: currentUser?.role || 0,
    brands: currentUser?.brands
      ? currentUser.brands.map((userBrand: Brands) => {
          return {
            id: userBrand.id,
            siteId: userBrand.siteId,
            name: userBrand.name,
          }
        })
      : [],
    id: currentUser?.id || '',
  }

  const requiredString = Yup.string().required(t('error.requiredField'))

  const validationSchemaUser = Yup.object().shape({
    firstName: requiredString
      .max(45, ({ max }) =>
        t('messages.inputMaxLength', {
          field: t('labels.name'),
          max,
        }),
      )
      .matches(REGEX_INPUT, t('error.regexInput')),
    lastName: requiredString
      .max(45, ({ max }) =>
        t('messages.inputMaxLength', {
          field: t('labels.description'),
          max,
        }),
      )
      .matches(REGEX_INPUT, t('error.regexInput')),
    email: requiredString.matches(
      REGEX_EMAIL,
      t('error.invalidField', { field: t('labels.email') }),
    ),
    username: requiredString
      .matches(
        REGEX_USERNAME,
        t('error.invalidField', { field: t('labels.username') }),
      )
      .min(5, ({ min }) => t('error.minCharacters', { min }))
      .max(20, ({ max }) => t('error.maxCharacters', { max })),
    role: Yup.number().required(t('error.requiredField')),
    brands: Yup.array().min(1, t('error.requiredField')),
    password:
      formMode === FormStatusType.CREATE
        ? getValidatePassword(true)
        : getValidatePassword(false),
    newPassword: Yup.string().when('password', {
      is: (password: string) => password && password.length > 0,
      then: getValidatePassword(false).oneOf(
        [Yup.ref('password'), null],
        t('error.passwordDontMatch'),
      ),
    }),
  })

  const formikUser = useFormik({
    initialValues: initialValuesUser,
    validationSchema: validationSchemaUser,
    onSubmit: () => {},
    enableReinitialize: true,
  })

  useEffect(() => {
    formikUser.resetForm()
  }, [dialog]) // eslint-disable-line

  return {
    formMode,
    dialog,
    formikUser,
    handleSubmit,
    handleCloseDialog,
  }
}
